import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import UserAvatar from "../UserAvatar";
import {
  RefreshCw,
  Search,
  X,
  Shield,
  ShieldOff,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Building2,
  Plus,
  UserPlus,
  Mail,
  Eye,
  EyeOff,
  Edit,
  Trash2,
} from "lucide-react";
import axios from "axios";
import AuthContext from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "";
const USERS_PER_PAGE = 10;

// Confirmation Modal
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmLabel, isSubmitting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-6 z-10"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-opacity disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const SuperUsersSection = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, user: null, action: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    email: "",
    full_name: "",
    role: "user",
    is_active: true,
    organization_id: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [addForm, setAddForm] = useState({
    email: "",
    full_name: "",
    password: "",
    confirmPassword: "",
    organization_id: "",
    role: "user",
    is_active: true,
  });
  const [addErrors, setAddErrors] = useState({});

  const getToken = () => localStorage.getItem("token");
  const totalPages = Math.ceil(usersCount / USERS_PER_PAGE);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Fetch organizations for the filter dropdown
  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/admin/organizations/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setOrganizations(response.data || []);
    } catch (err) {
      // silently fail — dropdown just won't populate
    }
  };

  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const skip = (currentPage - 1) * USERS_PER_PAGE;
      // Super-admin panel: opt into the platform-wide cross-org view.
      const params = { skip, limit: USERS_PER_PAGE, all_organizations: true };
      if (searchQuery) params.search = searchQuery;
      if (selectedOrgId) params.organization_id = selectedOrgId;

      const response = await axios.get(`${API_URL}/api/v1/users/`, {
        params,
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(response.data.data || []);
      setUsersCount(response.data.count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load users");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, selectedOrgId]);

  const handleSearchInput = (value) => {
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleToggleSuperuser = (user) => {
    const action = user.is_superuser ? "revoke" : "grant";
    setConfirmModal({
      isOpen: true,
      user,
      action,
    });
  };

  const confirmToggleSuperuser = async () => {
    const { user, action } = confirmModal;
    if (!user) return;

    setIsSubmitting(true);
    try {
      const newValue = action === "grant";
      await axios.patch(
        `${API_URL}/api/v1/users/${user.id}`,
        { is_superuser: newValue },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      showNotification(
        `Superuser access ${action === "grant" ? "granted to" : "revoked from"} ${user.full_name || user.email}`
      );
      setConfirmModal({ isOpen: false, user: null, action: null });
      fetchUsers();
    } catch (err) {
      showNotification(
        err.response?.data?.detail || `Failed to ${action} superuser access`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = (user) => {
    setDeleteModal({ isOpen: true, user });
  };

  const confirmDeleteUser = async () => {
    const { user } = deleteModal;
    if (!user) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/users/${user.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      showNotification(`User ${user.full_name || user.email} deleted successfully`);
      setDeleteModal({ isOpen: false, user: null });
      fetchUsers();
    } catch (err) {
      showNotification(
        err.response?.data?.detail || "Failed to delete user",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openAddModal = () => {
    setAddForm({
      email: "",
      full_name: "",
      password: "",
      confirmPassword: "",
      organization_id: "",
      role: "user",
      is_active: true,
    });
    setAddErrors({});
    setShowPassword(false);
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setAddErrors({});
    setShowPassword(false);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || "",
      full_name: user.full_name || "",
      role: user.role || "user",
      is_active: user.is_active !== false,
      organization_id: user.organization_id || "",
    });
    setEditErrors({});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setEditErrors({});
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!editForm.email) errors.email = "Email is required";
    if (!editForm.full_name) errors.full_name = "Full name is required";

    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        email: editForm.email,
        full_name: editForm.full_name,
        role: editForm.role,
        is_active: editForm.is_active,
      };
      if (editForm.organization_id) {
        payload.organization_id = editForm.organization_id;
      }
      await axios.patch(
        `${API_URL}/api/v1/users/${editingUser.id}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      showNotification(`User ${editForm.full_name || editForm.email} updated successfully`);
      closeEditModal();
      fetchUsers();
    } catch (err) {
      showNotification(
        err.response?.data?.detail || "Failed to update user",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!addForm.email) errors.email = "Email is required";
    if (!addForm.full_name) errors.full_name = "Full name is required";
    if (!addForm.password) errors.password = "Password is required";
    if (addForm.password.length < 6) errors.password = "Password must be at least 6 characters";
    if (addForm.password !== addForm.confirmPassword) errors.confirmPassword = "Passwords do not match";
    if (!addForm.organization_id) errors.organization_id = "Organization is required";

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/v1/users/`,
        {
          email: addForm.email,
          full_name: addForm.full_name,
          password: addForm.password,
          organization_id: addForm.organization_id,
          role: addForm.role,
          is_active: addForm.is_active,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      showNotification(`User ${addForm.full_name || addForm.email} created successfully`);
      closeAddModal();
      fetchUsers();
    } catch (err) {
      showNotification(
        err.response?.data?.detail || "Failed to create user",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg ${
            notification.type === "error"
              ? "bg-red-500 text-white"
              : "bg-green-500 text-white"
          }`}
        >
          {notification.type === "error" ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle2 className="w-5 h-5" />
          )}
          {notification.message}
        </motion.div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Superuser Management</h2>
          <p className="text-sm text-gray-500">
            Grant or revoke superuser access across the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
          <button
            onClick={fetchUsers}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => handleSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Organization Filter */}
        <div className="relative sm:w-64">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={selectedOrgId}
            onChange={(e) => {
              setSelectedOrgId(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchTerm ? "No users match your search." : "No users found."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Organization</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Superuser</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar
                          src={user.avatar_url}
                          name={user.full_name || user.email}
                          className="w-10 h-10 rounded-full object-cover"
                          textClassName="text-sm"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.full_name || "—"}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {user.organization_name || "—"}
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === "admin"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {user.role === "admin" ? "Org Admin" : "User"}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_superuser
                            ? "bg-amber-100 text-amber-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {user.is_superuser ? (
                          <>
                            <Shield className="w-3 h-3" />
                            Superuser
                          </>
                        ) : (
                          "Standard"
                        )}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(user)}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                          title="Edit user"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleSuperuser(user)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            user.is_superuser
                              ? "text-red-600 hover:bg-red-50"
                              : "text-purple-600 hover:bg-purple-50"
                          }`}
                          title={user.is_superuser ? "Revoke Superuser" : "Grant Superuser"}
                        >
                          {user.is_superuser ? (
                            <>
                              <ShieldOff className="w-4 h-4" />
                              Revoke
                            </>
                          ) : (
                            <>
                              <Shield className="w-4 h-4" />
                              Grant
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.id === currentUser?.id}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title={
                            user.id === currentUser?.id
                              ? "You cannot delete your own account"
                              : "Delete user"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-full font-medium transition-colors ${
                        currentPage === page
                          ? "org-gradient text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, user: null, action: null })}
        onConfirm={confirmToggleSuperuser}
        title={confirmModal.action === "grant" ? "Grant Superuser Access" : "Revoke Superuser Access"}
        message={
          confirmModal.action === "grant"
            ? `Are you sure you want to grant superuser access to ${confirmModal.user?.full_name || confirmModal.user?.email}? They will have full platform-wide admin privileges.`
            : `Are you sure you want to revoke superuser access from ${confirmModal.user?.full_name || confirmModal.user?.email}?`
        }
        confirmLabel={confirmModal.action === "grant" ? "Grant Access" : "Revoke Access"}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to permanently delete ${deleteModal.user?.full_name || deleteModal.user?.email}? This action cannot be undone.`}
        confirmLabel="Delete User"
        isSubmitting={isSubmitting}
      />

      {/* Edit User Modal */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={closeEditModal} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl z-10"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl org-gradient-br flex items-center justify-center">
                    <Edit className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
                    <p className="text-sm text-gray-500">{editingUser.email}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleEditUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  {editErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{editErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {editErrors.full_name && (
                    <p className="text-xs text-red-500 mt-1">{editErrors.full_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={editForm.organization_id}
                      onChange={(e) => setEditForm({ ...editForm, organization_id: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white"
                    >
                      <option value="">No organization</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.role === "admin"}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.checked ? "admin" : "user" })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Org Admin</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={closeAddModal} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl z-10"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl org-gradient-br flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Add User</h2>
                    <p className="text-sm text-gray-500">Create a new user in an organization</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAddUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={addForm.email}
                      onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  {addErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{addErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={addForm.full_name}
                    onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="John Doe"
                  />
                  {addErrors.full_name && (
                    <p className="text-xs text-red-500 mt-1">{addErrors.full_name}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={addForm.password}
                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Min 6 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                    {addErrors.password && (
                      <p className="text-xs text-red-500 mt-1">{addErrors.password}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={addForm.confirmPassword}
                        onChange={(e) => setAddForm({ ...addForm, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Repeat password"
                      />
                    </div>
                    {addErrors.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">{addErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={addForm.organization_id}
                      onChange={(e) => setAddForm({ ...addForm, organization_id: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white"
                    >
                      <option value="">Select an organization</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {addErrors.organization_id && (
                    <p className="text-xs text-red-500 mt-1">{addErrors.organization_id}</p>
                  )}
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addForm.role === "admin"}
                      onChange={(e) => setAddForm({ ...addForm, role: e.target.checked ? "admin" : "user" })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Org Admin</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addForm.is_active}
                      onChange={(e) => setAddForm({ ...addForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperUsersSection;
